/**
 * ARVEXIS MESSAGE HISTORY
 * =======================
 *
 * The member's written conversations with Arvexis, newest first.
 *
 * G2-C1 and the Phase 6 pass hold the same line: a row states only what the session itself
 * already knows — that it is a conversation, and when it happened. No summary, no excerpt, no
 * inferred topic, no generated title, no classification, no search, no filters, no archive,
 * no pinning, no swipe actions. Nothing here is persisted or intelligent.
 *
 * Ordering, contents, timestamps (member time-format preference) and loading behaviour are
 * unchanged. Phase 6 brought the canonical surface, the approved desktop shell and the flat
 * RECENT CONVERSATIONS list the approved reference shows.
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { memberTimeOptions } from '../utils/memberTime';
import { ChevronLeft, ChevronDown, ChevronRight, Loader2, MessageCircle } from 'lucide-react';
import DreamsShell from './DreamsShell';
import useFullScreenSurface from '../hooks/useFullScreenSurface';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const PAGE = 5;

// The row's only secondary metadata — the session's existing localized date and time.
// Read from authoritative session metadata; never generated, never persisted.
const formatSessionStamp = (iso, lang) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const locale = lang === 'es' ? 'es-ES' : 'en-US';
  return `${d.toLocaleDateString(locale, { month: 'short', day: 'numeric' })} · `
    + `${d.toLocaleTimeString(locale, memberTimeOptions({ hour: 'numeric', minute: '2-digit' }))}`;
};

const ConversationRow = ({ session, lang, index, onResume }) => (
  <motion.button
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: Math.min(index, 6) * 0.03 }}
    onClick={() => onResume(session)}
    className="mh__row"
    aria-label={`${lang === 'es' ? 'Conversación' : 'Conversation'} — ${formatSessionStamp(session.started_at, lang)}`}
    data-testid="reflection-card"
  >
    <span className="mh__glyph"><MessageCircle size={20} strokeWidth={1.5} /></span>
    <span className="mh__body">
      <span className="mh__label" data-testid="reflection-row-label">
        {lang === 'es' ? 'Conversación' : 'Conversation'}
      </span>
      <span className="mh__meta" data-testid="reflection-row-meta">
        {formatSessionStamp(session.started_at, lang)}
      </span>
    </span>
    <ChevronRight className="mh__chevron" strokeWidth={1.6} />
  </motion.button>
);

const PastReflections = ({ lang = 'en', onClose, onResumeSession }) => {
  useFullScreenSurface(true, onClose);
  const [isLoading, setIsLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [visible, setVisible] = useState(PAGE);
  const [error, setError] = useState(null);

  useEffect(() => { loadReflections(); }, []);

  const loadReflections = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/companion/sessions?limit=50`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load reflections');
      const data = await res.json();
      const ordered = [...(data.sessions || [])].sort(
        (a, b) => new Date(b.started_at) - new Date(a.started_at));
      setSessions(ordered);
    } catch (err) {
      console.error('Load reflections error:', err);
      setError(lang === 'es' ? 'No se pudieron cargar las conversaciones' : 'Could not load conversations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResume = (session) => {
    onResumeSession?.(session.session_id);
  };

  const shown = sessions.slice(0, visible);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      // the foreground is handed back at once: fading out cross-faded the still-mounted page beneath through this surface
      className="utility-overlay z-[150] utility-surface utility-surface--atmospheric"
      data-testid="past-reflections-modal"
    >
      <DreamsShell lang={lang} activePath="/profile">
        <header
          className="flex items-center gap-4 px-5 account-mobile-header"
          style={{
            paddingTop: 'calc(env(safe-area-inset-top, 47px) + 12px)',
            paddingBottom: '12px'
          }}
        >
          <button
            onClick={onClose}
            className="rounded-full flex items-center justify-center shrink-0"
            style={{ width: 44, height: 44, background: 'transparent',
                     border: '1px solid rgba(232, 197, 143, 0.38)' }}
            aria-label={lang === 'es' ? 'Volver' : 'Back'}
            data-testid="past-reflections-close"
          >
            <ChevronLeft className="w-5 h-5" style={{ color: 'rgba(232, 197, 143, 0.9)' }} />
          </button>
          <span
            className="account-wordmark"
            style={{ fontSize: '12px', letterSpacing: '0.42em', color: 'rgba(244, 239, 231, 0.8)',
                     margin: '0 auto', paddingRight: '44px' }}
            data-testid="message-history-wordmark"
          >
            ARVEXIS
          </span>
        </header>

        <div className="utility-head account-col" data-testid="message-history-head">
          <p className="utility-head__eyebrow" data-testid="message-history-eyebrow">
            {lang === 'es' ? 'HISTORIAL DE MENSAJES' : 'MESSAGE HISTORY'}
          </p>
          <h1 className="utility-head__title" data-testid="message-history-title">
            {lang === 'es' ? 'Historial de Mensajes' : 'Message History'}
          </h1>
          <p className="utility-head__sub" data-testid="message-history-subtitle">
            {lang === 'es'
              ? 'Tus conversaciones escritas con Arvexis.'
              : 'Your written conversations with Arvexis.'}
          </p>
        </div>

        <main className="account-col" style={{ padding: '4px 20px 48px' }} data-testid="message-history-main">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#E8C58F' }} />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="mh__empty">{error}</p>
              <button onClick={loadReflections} className="mh__retry" data-testid="message-history-retry">
                {lang === 'es' ? 'Intentar de nuevo' : 'Try again'}
              </button>
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12" data-testid="message-history-empty">
              <div className="mh__emptyglyph"><MessageCircle size={22} strokeWidth={1.5} /></div>
              <p className="mh__empty">
                {lang === 'es'
                  ? 'Aún no hay conversaciones escritas. Las que tengas aparecerán aquí.'
                  : 'No written conversations yet. The ones you have will appear here.'}
              </p>
            </div>
          ) : (
            <>
              <p className="mh__section" data-testid="recent-conversations-label">
                {lang === 'es' ? 'CONVERSACIONES RECIENTES' : 'RECENT CONVERSATIONS'}
              </p>
              <div className="mh__stack">
                {shown.map((session, idx) => (
                  <ConversationRow
                    key={session.session_id || idx}
                    session={session}
                    index={idx}
                    lang={lang}
                    onResume={handleResume}
                  />
                ))}
              </div>
              {sessions.length > visible && (
                <button
                  onClick={() => setVisible((v) => v + PAGE)}
                  className="mh__more"
                  data-testid="load-more-conversations-btn"
                >
                  <ChevronDown size={17} strokeWidth={1.7} />
                  <span>{lang === 'es' ? 'Cargar más conversaciones' : 'Load more conversations'}</span>
                </button>
              )}
            </>
          )}
        </main>
      </DreamsShell>

      <style>{`
        .mh__section {
          font-size: 10.5px; font-weight: 600; letter-spacing: 0.24em; text-transform: uppercase;
          color: rgba(232, 197, 143, 0.82); margin: 6px 0 12px;
          font-family: 'Inter', system-ui, sans-serif;
        }
        .mh__stack { display: flex; flex-direction: column; gap: 10px; }
        .mh__row {
          display: flex; align-items: center; gap: 14px; width: 100%; padding: 14px 16px;
          border-radius: 18px; background: rgba(255, 255, 255, 0.028);
          border: 1px solid rgba(244, 239, 231, 0.10); cursor: pointer; text-align: left;
          font-family: 'Inter', system-ui, sans-serif; min-height: 76px;
          transition: background-color 180ms ease, border-color 180ms ease;
        }
        .mh__row:hover {
          background: rgba(255, 255, 255, 0.05); border-color: rgba(232, 197, 143, 0.26);
        }
        .mh__glyph {
          flex-shrink: 0; width: 44px; height: 44px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          border: 1px solid rgba(232, 197, 143, 0.42); color: #E8C58F;
        }
        .mh__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 5px; }
        .mh__label { font-size: 16px; color: #F4EFE7; }
        .mh__meta { font-size: 13.5px; color: rgba(244, 239, 231, 0.48); }
        .mh__chevron {
          width: 18px; height: 18px; flex-shrink: 0; color: rgba(244, 239, 231, 0.34);
        }
        .mh__more {
          display: flex; align-items: center; justify-content: center; gap: 9px; width: 100%;
          min-height: 52px; margin-top: 12px; border-radius: 16px; font-size: 14.5px;
          background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(244, 239, 231, 0.12);
          color: rgba(244, 239, 231, 0.82); cursor: pointer;
          font-family: 'Inter', system-ui, sans-serif;
          transition: background-color 180ms ease, border-color 180ms ease;
        }
        .mh__more:hover {
          background: rgba(232, 197, 143, 0.08); border-color: rgba(232, 197, 143, 0.3);
        }
        .mh__empty {
          font-size: 14px; line-height: 1.6; color: rgba(244, 239, 231, 0.45);
          font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic;
        }
        .mh__emptyglyph {
          width: 52px; height: 52px; margin: 0 auto 16px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          border: 1px solid rgba(232, 197, 143, 0.3); color: rgba(232, 197, 143, 0.7);
        }
        .mh__retry {
          margin-top: 12px; font-size: 13px; color: #E8C58F; background: none; border: none;
          cursor: pointer; text-decoration: underline;
        }
      `}</style>
    </motion.div>
  );
};

export default PastReflections;
